import extend from 'lodash/extend';
import isEmpty from 'lodash/isEmpty';
import { ComponentFactoryResolver, Injectable } from '@angular/core';
import { BuildCakeModalComponent } from './build-cake-modal/build-cake-modal.component';
import { IMpOutlet } from '@shared/models/outlet/i-mp-outlet';
import { OutletsMapModalComponent } from './outlets-map-modal/outlets-map-modal.component';
import { CartResetWarningModalComponent } from './cart-reset-warning-modal/cart-reset-warning-modal.component';
import { UserAuthModalComponent } from './user-auth-modal/user-auth-modal.component';
import { ContactUsModalComponent } from './contact-us-modal/contact-us-modal.component';
import {
  CieUtilsModalManagerService,
  IBakery,
  IBakeryCountry,
  IOutletDelivery,
  IScheduleOverride,
  IProductTypeOverview,
  MODAL
} from '@cakeiteasy/web-utils';
import { CheckDeliveryModalComponent, IUser, OutletScheduleOverridesFacadeService } from '@cakeiteasy/order-process';
import { DeliveryPriceCheckModalComponent } from '@modals/delivery-price-check-modal/delivery-price-check-modal.component';
import { HowItWorksModalComponent } from '@modals/how-it-works-modal/how-it-works-modal.component';
import { ThirdPartyInfoModalComponent } from '@modals/third-party-info-modal/third-party-info-modal.component';
import { StoresMapModalComponent } from '@cakeiteasy/web-ui';

@Injectable()
export class ModalOpenerService {
  constructor(private _modalManager: CieUtilsModalManagerService,
              private _outletScheduleOverrides: OutletScheduleOverridesFacadeService) {
  }

  showStoresMap(componentResolver: ComponentFactoryResolver, data: {
    outlets: IOutletDelivery[],
    disabledOutlets: any[],
  }) {
    return this._modalManager.show(StoresMapModalComponent, componentResolver, {
      ...data,
      loadScheduleOverrides: (outlet: IOutletDelivery) => this.loadOutletScheduleOverrides(outlet),
    }, {
      windowClass: 'modal-full-width',
      backdrop: 'static'
    });
  }

  public openCakeDetails(componentResolver: ComponentFactoryResolver,
                         data: {
                           productTypeId: number,
                           orderLineBuilderId?: string,
                         }, user?: IUser): Promise<any> {
    return this._modalManager.show(BuildCakeModalComponent,
      componentResolver, {
        typeId: data.productTypeId,
        orderLineBuilderId: data.orderLineBuilderId,
        user
      }, {
        backdrop: 'static',
        windowClass: 'cake-builder-modal',
        size: 'lg'
      });
  }

  public showThirdPartyInfoModal(componentResolver: ComponentFactoryResolver) {
    return this._modalManager.show(ThirdPartyInfoModalComponent, componentResolver, null,
      { windowClass: 'modal-simple md modal-delivery-check' });
  }

  public showOutletsMap(componentResolver: ComponentFactoryResolver,
                        outlets: IMpOutlet[],
                        outletsSubset: IMpOutlet[],
                        config?: any
  ): Promise<any> {
    return this._modalManager.show(OutletsMapModalComponent, componentResolver,
      { outlets, outletsSubset, config },
      { windowClass: 'modal-full-width' });
  }

  private async loadOutletScheduleOverrides(outlet: IOutletDelivery): Promise<IScheduleOverride[]> {
    if (!outlet) {
      return Promise.resolve([]);
    }

    await this._outletScheduleOverrides.ensure(outlet.bakery.id, outlet.id);
    return this._outletScheduleOverrides.scheduleOverridesByOutletId[outlet.id] || [];
  }

  public showDeliveryPriceCheck(componentResolver: ComponentFactoryResolver,
                                params: { country: IBakeryCountry, bakeryId: number }): Promise<any> {
    return this._modalManager.show(DeliveryPriceCheckModalComponent, componentResolver,
      params, { windowClass: 'modal-simple modal-delivery-check' });
  }

  public showCheckDeliveryModal(componentResolver: ComponentFactoryResolver,
                                params: {
                                  productType: IProductTypeOverview,
                                  bakery: IBakery,
                                  bakeryOverviewUrl: string
                                }
  ) {
    return this._modalManager.show(CheckDeliveryModalComponent, componentResolver,
      params, { windowClass: 'modal-simple modal-delivery-check' });
  }

  public showBasketResetWarning(componentResolver: ComponentFactoryResolver): Promise<any> {
    return this._modalManager
      .show(CartResetWarningModalComponent, componentResolver, {}, { windowClass: 'modal-simple' })
      .catch(() => Promise.reject(MODAL.CLOSE_EVENTS.DISMISSED));
  }

  public showAuth(componentResolver: ComponentFactoryResolver, showedForm?: string, params?: any): Promise<any> {
    const formParams = !isEmpty(params) ? extend({}, { showedForm }, params) : { showedForm };
    return this._modalManager.show(UserAuthModalComponent, componentResolver, formParams,
      {
        backdrop: 'static',
        windowClass: 'modal-simple md'
      });
  }

  public showContactUs(componentResolver: ComponentFactoryResolver, showedForm?: string, params?: any): Promise<any> {
    const formParams = !isEmpty(params) ? extend({}, { showedForm }, params) : { showedForm };
    return this._modalManager.show(ContactUsModalComponent, componentResolver, formParams,
      {
        windowClass: 'modal-simple lg'
      });
  }

  public showHowItWorks(componentResolver: ComponentFactoryResolver): Promise<any> {
    return this._modalManager.show(HowItWorksModalComponent, componentResolver, null,
      {
        windowClass: 'modal-xl-4 how-it-works-modal'
      });
  }
}
